import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useOpsAgencyId } from '@/services/opsRota/agency';
import { opsRotaClient } from '@/services/opsRota/client';
import { opsRotaQueryKeys } from '@/services/opsRota/queryKeys';
import type {
  ManagerCreateInput,
  ManagerUpdateInput,
  ScheduleCreateInput,
  ScheduleRepeatInput,
  ScheduleUpdateInput,
} from '@/services/opsRota/types';

// Fresh enough for a page two admins may edit at once; the 409 path covers
// the rest. The managers lists use it too: under the app-wide Infinity
// staleTime, a manager another admin added would never show up.
export const OPS_ROTA_STALE_MS = 30_000;

// The schedules poll while the page is visible: meta.today (the today ring,
// the read-only rule) and each block's `editable` move at the ops-zone
// midnight even when nobody touches the page.
export const OPS_ROTA_REFRESH_MS = 60_000;

export type DateRange = { from: string; to: string };

/**
 * Blocks in [from, to]. `keepPrevious` is for the month grid only: it keeps
 * the dots (and `meta.today`) while the next month loads. Never for a single
 * day, or it would show the previous day's blocks under the new heading.
 * `carryOver` adds the previous day's overnight blocks that run into `from`,
 * for the day panel and the preview card, never the month grid.
 */
export const useOpsSchedules = (
  range: DateRange | null,
  {
    keepPrevious = false,
    carryOver = false,
  }: { keepPrevious?: boolean; carryOver?: boolean } = {},
) => {
  const agencyId = useOpsAgencyId();
  return useQuery({
    queryKey: opsRotaQueryKeys.schedules(range?.from ?? '', range?.to ?? '', {
      carryOver,
      agencyId,
    }),
    queryFn: () =>
      opsRotaClient.listSchedules({
        ...(range as DateRange),
        ...(carryOver ? { includeCarryOver: true } : {}),
      }),
    enabled: range !== null,
    staleTime: OPS_ROTA_STALE_MS,
    refetchInterval: OPS_ROTA_REFRESH_MS,
    refetchOnWindowFocus: true,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });
};

/** Active managers, for the schedule form's picker. */
export const useOpsManagers = () => {
  const agencyId = useOpsAgencyId();
  return useQuery({
    queryKey: opsRotaQueryKeys.managers(agencyId),
    queryFn: () => opsRotaClient.listManagers(),
    staleTime: OPS_ROTA_STALE_MS,
    refetchOnWindowFocus: true,
  });
};

/**
 * Every write refreshes the calendar dots, day panel, preview and bar
 * (OP-12). On failure too: after a 409/404 the page must reload the block,
 * or every retry resends the stale lock_version.
 */
export const useScheduleMutations = () => {
  const queryClient = useQueryClient();
  const onSettled = () =>
    queryClient.invalidateQueries({ queryKey: opsRotaQueryKeys.all });

  return {
    create: useMutation({
      mutationFn: (input: ScheduleCreateInput) =>
        opsRotaClient.createSchedule(input),
      onSettled,
    }),
    update: useMutation({
      mutationFn: ({ id, input }: { id: number; input: ScheduleUpdateInput }) =>
        opsRotaClient.updateSchedule(id, input),
      onSettled,
    }),
    remove: useMutation({
      mutationFn: ({ id, lockVersion }: { id: number; lockVersion: number }) =>
        opsRotaClient.deleteSchedule(id, lockVersion),
      onSettled,
    }),
    repeat: useMutation({
      mutationFn: ({ id, input }: { id: number; input: ScheduleRepeatInput }) =>
        opsRotaClient.repeatSchedule(id, input),
      onSettled,
    }),
  };
};

/** Every manager, active and inactive: the roster modal. */
export const useAllManagers = () => {
  const agencyId = useOpsAgencyId();
  return useQuery({
    queryKey: opsRotaQueryKeys.managersAll(agencyId),
    queryFn: () => opsRotaClient.listManagers({ status: 'all' }),
    staleTime: OPS_ROTA_STALE_MS,
    refetchOnWindowFocus: true,
  });
};

/**
 * Roster writes. Each one refreshes everything under `ops_rota`: the
 * picker's active list, the roster, the calendar flags (a deactivated
 * manager) and the dashboard bar.
 */
export const useManagerMutations = () => {
  const queryClient = useQueryClient();
  const onSettled = () =>
    queryClient.invalidateQueries({ queryKey: opsRotaQueryKeys.all });

  return {
    create: useMutation({
      mutationFn: (input: ManagerCreateInput) =>
        opsRotaClient.createManager(input),
      onSettled,
    }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: number; patch: ManagerUpdateInput }) =>
        opsRotaClient.updateManager(id, patch),
      onSettled,
    }),
    remove: useMutation({
      mutationFn: (id: number) => opsRotaClient.deleteManager(id),
      onSettled,
    }),
  };
};
